import { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Switch, FormControlLabel, Typography, Alert, Box, CircularProgress,
} from "@mui/material";
import { ACCENT } from "./accent";
import { PART_FIELDS, DRONE_FIELDS, buildPayloadFromFields } from "./partSpecs";
import { submitPartRequest } from "./api/publicSubmissions";
import { parseCurve } from "../motorData/parseCurve";

// Submission types: the four catalog parts + drone/frame (airframe preset).
const TYPES = [
  { value: "motor", label: "Motor" },
  { value: "propeller", label: "Propeller" },
  { value: "esc", label: "ESC" },
  { value: "battery", label: "Battery" },
  { value: "fcu", label: "Flight controller" },
  { value: "sensor", label: "Sensor" },
  { value: "drone", label: "Drone / frame" },
];

const fieldsFor = (type) => (type === "drone" ? DRONE_FIELDS : PART_FIELDS[type]);

/**
 * Public "add to the database" request form. Anyone can submit; entries land in
 * a moderation queue server-side (never straight into the catalog). Reuses the
 * same field specs as the authed catalog forms so submissions arrive complete.
 */
const SubmitPartDialog = ({ open, onClose }) => {
  const [type, setType] = useState("motor");
  const [values, setValues] = useState({});
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  // Manufacturer's published thrust table, pasted from a datasheet. Motors only
  // — a curve belongs to a motor+propeller pair, not to an ESC or a battery.
  const [curveText, setCurveText] = useState("");
  const [testedProp, setTestedProp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [doneId, setDoneId] = useState(null);

  const fields = fieldsFor(type);
  const curve = type === "motor" && curveText.trim() ? parseCurve(curveText) : null;
  const set = (key) => (v) => setValues((s) => ({ ...s, [key]: v }));
  const canSubmit = String(values.brand || "").trim() && String(values.name || "").trim();

  const reset = () => {
    setValues({});
    setError(null);
    setDoneId(null);
    setNotes("");
    setCurveText("");
    setTestedProp("");
  };

  const close = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = buildPayloadFromFields(fields, values);
      if (curve?.rows?.length) {
        // Goes into the moderation queue as opaque JSON alongside the specs; a
        // reviewer publishes it as the combination's `claimed` curve.
        payload.claimed_curve = curve.rows;
        payload.tested_propeller = testedProp.trim() || null;
      }
      const { submission_id } = await submitPartRequest({
        type,
        payload,
        contact_email: email.trim(),
        notes: notes.trim(),
      });
      setDoneId(submission_id || "pending");
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Submit to the public database</DialogTitle>
      <DialogContent>
        {doneId ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Thanks! Your submission is in the review queue — once verified it becomes available to
            every builder using the calculator.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Know a part that's missing? Send its specs — submissions are reviewed before they
              appear in the shared catalog. Brand and model are required; every extra spec improves
              the simulation for everyone.
            </Typography>
            <TextField
              select fullWidth size="small" label="What are you adding?" value={type}
              onChange={(e) => { setType(e.target.value); setValues({}); }}
              sx={{ mb: 2 }}
            >
              {TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {fields.map((f) =>
                f.type === "bool" ? (
                  <FormControlLabel
                    key={f.key}
                    sx={{ flex: "1 1 45%", ml: 0 }}
                    control={<Switch size="small" checked={Boolean(values[f.key])} onChange={(e) => set(f.key)(e.target.checked)} />}
                    label={<Typography variant="body2">{f.label}</Typography>}
                  />
                ) : f.type === "select" ? (
                  <TextField
                    key={f.key} select size="small" label={f.label} value={values[f.key] ?? ""}
                    onChange={(e) => set(f.key)(e.target.value)} sx={{ flex: "1 1 45%", minWidth: 180 }}
                  >
                    {f.options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </TextField>
                ) : (
                  <TextField
                    key={f.key} size="small" label={`${f.label}${f.required ? " *" : ""}`}
                    type={f.type === "number" ? "number" : "text"} value={values[f.key] ?? ""}
                    onChange={(e) => set(f.key)(e.target.value)} sx={{ flex: "1 1 45%", minWidth: 180 }}
                  />
                )
              )}
            </Box>

            {type === "motor" && (
              <Box sx={{ mt: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Published thrust data (optional)
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                  Paste your datasheet's test table — include the column headers. It's published as
                  your claim, side by side with our thrust-stand measurement once we bench it.
                </Typography>
                <TextField
                  fullWidth size="small" value={testedProp} label="Propeller used for this data"
                  onChange={(e) => setTestedProp(e.target.value)} sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth size="small" multiline minRows={4} value={curveText}
                  placeholder={"Throttle\tCurrent (A)\tRPM\tThrust (gf)\tPower (W)\tEfficiency (g/W)\n50%\t28.14\t1575\t22744\t2590\t8.78"}
                  onChange={(e) => setCurveText(e.target.value)}
                  sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12 } }}
                />
                {curve?.error && <Alert severity="warning" sx={{ mt: 1 }}>{curve.error}</Alert>}
                {curve?.rows?.length > 0 && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Parsed {curve.rows.length} throttle points ({curve.rows[0].throttle}%–
                    {curve.rows[curve.rows.length - 1].throttle}%).
                  </Alert>
                )}
              </Box>
            )}

            <TextField
              fullWidth size="small" label="Your email (optional — for follow-up questions)" value={email}
              onChange={(e) => setEmail(e.target.value)} sx={{ mt: 2 }}
            />
            <TextField
              fullWidth size="small" multiline minRows={2} value={notes}
              label="Notes / source link (datasheet, store page…)"
              onChange={(e) => setNotes(e.target.value)} sx={{ mt: 1.5 }}
            />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={submitting} sx={{ textTransform: "none" }}>
          {doneId ? "Close" : "Cancel"}
        </Button>
        {!doneId && (
          <Button
            onClick={submit} variant="contained" disabled={!canSubmit || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: ACCENT, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: ACCENT, filter: "brightness(0.95)" } }}
          >
            Submit for review
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubmitPartDialog;
